import React from "react";
import {
    Box,
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    Button,
    Stack,
    Icon
} from "@chakra-ui/react";
import { MdEmail, MdLock, MdOutlineVisibility, MdOutlineVisibilityOff, MdLogin } from "react-icons/md";
import { getInputProps, getSubmitButtonProps } from "./loginStyles";

const ICON_COLOR = "rgba(255, 255, 255, 0.5)";
const TOGGLE_ICON_COLOR = "rgba(255, 255, 255, 0.8)";

/**
 * Renders the email input field for login.
 * @param {Object} props
 * @param {string} props.email - Current email value
 * @param {Function} props.setEmail - Email state setter
 * @param {boolean} props.loading - Whether the form is submitting
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function EmailField({ email, setEmail, loading, isMobile }) {
    return (
        <InputGroup size="lg">
            <InputLeftElement pointerEvents="none">
                <Icon as={MdEmail} color={ICON_COLOR} fontSize={isMobile ? "20px" : "22px"} />
            </InputLeftElement>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Email" isDisabled={loading} {...getInputProps(isMobile)} />
        </InputGroup>
    );
}

/**
 * Renders the visibility toggle button for the password field.
 * @param {Object} props
 * @param {boolean} props.showPassword - Whether the password is visible
 * @param {Function} props.setShowPassword - Toggle callback
 * @param {boolean} props.loading - Whether the form is submitting
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function VisibilityToggle({ showPassword, setShowPassword, loading, isMobile }) {
    const toggleIcon = showPassword ? MdOutlineVisibilityOff : MdOutlineVisibility;
    return (
        <InputRightElement width="3rem" h="100%">
            <Icon as={toggleIcon} boxSize={isMobile ? "20px" : "22px"} color={TOGGLE_ICON_COLOR}
                role="button" aria-label="Afficher le mot de passe"
                cursor={loading ? "not-allowed" : "pointer"}
                onClick={() => !loading && setShowPassword(!showPassword)}
                _hover={{ color: "rgba(255, 255, 255, 1)" }} />
        </InputRightElement>
    );
}

/**
 * Renders the password input field with visibility toggle for login.
 * @param {Object} props - password, setPassword, showPassword, setShowPassword, loading, isMobile
 * @returns {React.ReactElement}
 */
function PasswordField({ password, setPassword, showPassword, setShowPassword, loading, isMobile }) {
    return (
        <InputGroup size="lg">
            <InputLeftElement pointerEvents="none">
                <Icon as={MdLock} color={ICON_COLOR} fontSize={isMobile ? "20px" : "22px"} />
            </InputLeftElement>
            <Input type={showPassword ? "text" : "password"} value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="Mot de passe"
                isDisabled={loading} {...getInputProps(isMobile)} />
            <VisibilityToggle showPassword={showPassword} setShowPassword={setShowPassword}
                loading={loading} isMobile={isMobile} />
        </InputGroup>
    );
}

/**
 * Renders the login form with email, password fields and submit button.
 * @param {Object} props - email, setEmail, password, setPassword, showPassword,
 *   setShowPassword, loading, isMobile, onSubmit
 * @returns {React.ReactElement}
 */
function LoginForm({ email, setEmail, password, setPassword, showPassword, setShowPassword, loading, isMobile, onSubmit }) {
    return (
        <Box p={isMobile ? "24px 20px" : "32px 24px"}>
            <Stack spacing="12px">
                <EmailField email={email} setEmail={setEmail} loading={loading} isMobile={isMobile} />
                <PasswordField password={password} setPassword={setPassword} showPassword={showPassword}
                    setShowPassword={setShowPassword} loading={loading} isMobile={isMobile} />
                <Button onClick={onSubmit} isLoading={loading} loadingText="Connexion..."
                    isDisabled={loading || !email?.length || !password?.length}
                    leftIcon={<Icon as={MdLogin} />} {...getSubmitButtonProps(isMobile)}>
                    Se connecter
                </Button>
            </Stack>
        </Box>
    );
}

export default LoginForm;
